import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, ArrowDown, Calculator, CreditCard, Banknote, Percent, TrendingUp } from 'lucide-react';

const AED_USD_RATE = 3.674;
const CONSULTATION_FEE_USD = 250;
const TABBY_FIRST_PAYMENT_PCT = 0.40;
const TAMARA_FIRST_PAYMENT_PCT = 0.25;

const fmtAED = (v) => `AED ${(v || 0).toLocaleString('en-AE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtUSD = (v) => `$ ${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function BDCalculator() {
    const [amountInHand, setAmountInHand] = useState('');
    const [currency, setCurrency] = useState('AED');
    const [paymentMethod, setPaymentMethod] = useState(null);

    const calc = useMemo(() => {
        const raw = parseFloat(amountInHand) || 0;
        if (raw <= 0) return null;

        const amountAED = currency === 'USD' ? raw * AED_USD_RATE : raw;
        const consultationFeeAED = CONSULTATION_FEE_USD * AED_USD_RATE;

        // ── DIRECT REDEPOSIT (no fee, full amount → MT5) ──
        const directRedepositUSD = amountAED / AED_USD_RATE;

        // ── MIX (fee deducted, then 50/50 split) ──
        const afterFee = Math.max(0, amountAED - consultationFeeAED);
        const split1AED = afterFee / 2;
        const split2AED = afterFee / 2;
        const split1USD = split1AED / AED_USD_RATE;

        let split2FullAED = 0;
        let split2FullUSD = 0;
        let methodLabel = '';
        let methodPct = 0;

        if (paymentMethod === 'tabby') {
            split2FullAED = split2AED / TABBY_FIRST_PAYMENT_PCT;
            split2FullUSD = split2FullAED / AED_USD_RATE;
            methodLabel = 'Tabby (40% first payment)';
            methodPct = TABBY_FIRST_PAYMENT_PCT;
        } else if (paymentMethod === 'tamara') {
            split2FullAED = split2AED / TAMARA_FIRST_PAYMENT_PCT;
            split2FullUSD = split2FullAED / AED_USD_RATE;
            methodLabel = 'Tamara (25% first payment)';
            methodPct = TAMARA_FIRST_PAYMENT_PCT;
        } else if (paymentMethod === 'direct') {
            split2FullAED = split2AED;
            split2FullUSD = split2AED / AED_USD_RATE;
            methodLabel = 'Direct Payment';
            methodPct = 1;
        }

        const totalEquityAED = split1AED + split2FullAED;
        const totalEquityUSD = totalEquityAED / AED_USD_RATE;
        const benefitAED = totalEquityAED - amountAED;
        const benefitUSD = totalEquityUSD - directRedepositUSD;

        return {
            amountAED, consultationFeeAED, afterFee,
            directRedepositUSD,
            split1AED, split1USD,
            split2AED, split2FullAED, split2FullUSD,
            totalEquityAED, totalEquityUSD,
            methodLabel, methodPct,
            benefitAED, benefitUSD,
        };
    }, [amountInHand, currency, paymentMethod]);

    const currencyIcon = currency === 'AED' ? 'AED' : '$';

    return (
        <Card className="border-2 border-dashed border-sky-200 dark:border-sky-800" data-testid="bd-calculator">
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    <Calculator className="h-5 w-5 text-sky-500" />
                    BD Equity Calculator
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Input */}
                <div className="space-y-2">
                    <Label className="text-sm font-medium">Amount in Hand (Customer)</Label>
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">{currencyIcon}</span>
                            <Input
                                type="number"
                                placeholder={currency === 'AED' ? 'Enter amount in AED...' : 'Enter amount in USD...'}
                                value={amountInHand}
                                onChange={e => setAmountInHand(e.target.value)}
                                className={`${currency === 'AED' ? 'pl-12' : 'pl-8'} text-lg font-mono`}
                                data-testid="bd-calc-amount-input"
                            />
                        </div>
                        <div className="flex rounded-lg border overflow-hidden">
                            <button onClick={() => setCurrency('AED')}
                                className={`px-3 py-2 text-sm font-medium transition-colors ${currency === 'AED' ? 'bg-sky-500 text-white' : 'bg-muted hover:bg-accent'}`}
                                data-testid="bd-calc-currency-aed">AED</button>
                            <button onClick={() => setCurrency('USD')}
                                className={`px-3 py-2 text-sm font-medium transition-colors ${currency === 'USD' ? 'bg-sky-500 text-white' : 'bg-muted hover:bg-accent'}`}
                                data-testid="bd-calc-currency-usd">USD</button>
                        </div>
                    </div>
                </div>

                {calc && (
                    <>
                        {/* ══ COMPARISON: Direct Redeposit vs Mix ══ */}
                        <div className="grid grid-cols-2 gap-3">
                            {/* LEFT: Direct Redeposit (no fee) */}
                            <div className="p-4 bg-muted/60 rounded-xl border border-border relative">
                                <div className="absolute -top-2.5 left-3 px-2 bg-background text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                    Option A: Direct Redeposit
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-1">No consultation fee, full amount as redeposit</p>
                                <div className="mt-3 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Amount</span>
                                        <span className="font-mono">{fmtAED(calc.amountAED)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Fee</span>
                                        <span className="font-mono text-emerald-500">No charge</span>
                                    </div>
                                    <div className="border-t pt-2">
                                        <p className="text-[10px] text-muted-foreground uppercase">Total Equity in MT5</p>
                                        <p className="text-xl font-bold font-mono text-muted-foreground">{fmtUSD(calc.directRedepositUSD)}</p>
                                        <p className="text-xs font-mono text-muted-foreground">{fmtAED(calc.amountAED)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT: Mix (Redeposit + Upgrade) */}
                            <div className="p-4 bg-gradient-to-br from-sky-500/10 to-emerald-500/10 rounded-xl border-2 border-sky-400 dark:border-sky-600 relative">
                                <div className="absolute -top-2.5 left-3 px-2 bg-background text-[10px] font-semibold uppercase tracking-wider text-sky-600">
                                    Option B: Mix (Redeposit + Upgrade)
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-1">$250 fee, split 50/50 — upgrade via Tabby/Tamara</p>
                                <div className="mt-3 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Amount</span>
                                        <span className="font-mono">{fmtAED(calc.amountAED)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-red-500">
                                        <span>Consultation ($250)</span>
                                        <span className="font-mono">- {fmtAED(calc.consultationFeeAED)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">After Deduction</span>
                                        <span className="font-mono font-medium">{fmtAED(calc.afterFee)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ══ MIX SPLITS ══ */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
                                <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-medium">Split 1: Redeposit</p>
                                <p className="text-xs text-muted-foreground mt-0.5">Direct to MT5</p>
                                <p className="text-lg font-bold font-mono text-emerald-700 dark:text-emerald-400 mt-1">{fmtAED(calc.split1AED)}</p>
                                <p className="text-xs font-mono text-muted-foreground">{fmtUSD(calc.split1USD)}</p>
                            </div>
                            <div className="p-3 bg-sky-50 dark:bg-sky-950/30 rounded-lg border border-sky-200 dark:border-sky-800">
                                <p className="text-[10px] uppercase tracking-wider text-sky-600 font-medium">Split 2: Upgrade</p>
                                <p className="text-xs text-muted-foreground mt-0.5">Choose payment below</p>
                                <p className="text-lg font-bold font-mono text-sky-700 dark:text-sky-400 mt-1">{fmtAED(calc.split2AED)}</p>
                                <p className="text-xs font-mono text-muted-foreground">{fmtUSD(calc.split2AED / AED_USD_RATE)}</p>
                            </div>
                        </div>

                        {/* Payment Method Buttons */}
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Split 2 Payment Method:</Label>
                            <div className="grid grid-cols-3 gap-2">
                                <button onClick={() => setPaymentMethod('tabby')}
                                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-xs font-medium ${
                                        paymentMethod === 'tabby'
                                            ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 shadow-md'
                                            : 'border-muted hover:border-violet-300 hover:bg-violet-50/50'
                                    }`} data-testid="bd-calc-tabby">
                                    <CreditCard className="h-5 w-5" /><span>Tabby</span>
                                    <span className="text-[10px] text-muted-foreground">40% upfront</span>
                                </button>
                                <button onClick={() => setPaymentMethod('tamara')}
                                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-xs font-medium ${
                                        paymentMethod === 'tamara'
                                            ? 'border-pink-500 bg-pink-50 dark:bg-pink-950/30 text-pink-700 dark:text-pink-300 shadow-md'
                                            : 'border-muted hover:border-pink-300 hover:bg-pink-50/50'
                                    }`} data-testid="bd-calc-tamara">
                                    <Percent className="h-5 w-5" /><span>Tamara</span>
                                    <span className="text-[10px] text-muted-foreground">25% upfront</span>
                                </button>
                                <button onClick={() => setPaymentMethod('direct')}
                                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-xs font-medium ${
                                        paymentMethod === 'direct'
                                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 shadow-md'
                                            : 'border-muted hover:border-emerald-300 hover:bg-emerald-50/50'
                                    }`} data-testid="bd-calc-direct">
                                    <Banknote className="h-5 w-5" /><span>Direct</span>
                                    <span className="text-[10px] text-muted-foreground">Full amount</span>
                                </button>
                            </div>
                        </div>

                        {/* ══ RESULT + COMPARISON ══ */}
                        {paymentMethod && (
                            <div className="space-y-3">
                                {/* Mix result */}
                                <div className="p-4 bg-gradient-to-br from-sky-500/10 to-emerald-500/10 rounded-xl border border-sky-200 dark:border-sky-800 space-y-3">
                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        {calc.methodLabel}
                                    </p>
                                    {paymentMethod !== 'direct' && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="text-muted-foreground">Split 2 ({(calc.methodPct * 100)}% of total)</span>
                                            <ArrowRight className="h-3 w-3" />
                                            <span className="font-mono font-medium">100% = {fmtAED(calc.split2FullAED)}</span>
                                        </div>
                                    )}
                                    <div className="text-sm space-y-1 pt-2 border-t border-border/50">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Redeposit (MT5)</span>
                                            <span className="font-mono">{fmtAED(calc.split1AED)} ({fmtUSD(calc.split1USD)})</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Upgrade Credit (MT5)</span>
                                            <span className="font-mono">{fmtAED(calc.split2FullAED)} ({fmtUSD(calc.split2FullUSD)})</span>
                                        </div>
                                    </div>
                                    <div className="pt-3 border-t-2 border-sky-400 dark:border-sky-600">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-bold">Total Equity in MT5 (Mix)</span>
                                            <div className="text-right">
                                                <p className="text-xl font-bold font-mono text-sky-600 dark:text-sky-400">{fmtUSD(calc.totalEquityUSD)}</p>
                                                <p className="text-sm font-mono text-muted-foreground">{fmtAED(calc.totalEquityAED)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Benefit comparison banner */}
                                {paymentMethod !== 'direct' && calc.benefitAED > 0 && (
                                    <div className="p-3 bg-emerald-500/15 border border-emerald-500/30 rounded-xl">
                                        <div className="flex items-center gap-2">
                                            <TrendingUp className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                                            <div>
                                                <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                                                    Customer gets {fmtUSD(calc.benefitUSD)} MORE with Mix!
                                                </p>
                                                <p className="text-xs text-muted-foreground mt-0.5">
                                                    Direct Redeposit: {fmtUSD(calc.directRedepositUSD)} vs Mix ({calc.methodLabel.split(' ')[0]}): {fmtUSD(calc.totalEquityUSD)} — extra {fmtAED(calc.benefitAED)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {!calc && (
                    <div className="text-center py-6 text-sm text-muted-foreground">
                        Enter the customer's amount in hand to see the equity breakdown
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
